<div class="custom-table-container" [class]="tableClass()">
  <table class="custom-table w-full" [id]="id()">
    <!-- table header -->
    <thead class="bg-neutral-50" [class.sticky]="stickyHeader()">
      @if (parentColumns().length) {
        <tr class="border-b border-neutral-200">
          @for (
            column of parentColumns();
            track $index;
            let isFirst = $first;
            let isLast = $last
          ) {
            <ng-container>
              <th
                class="px-lg py-md text-sm font-medium border-neutral-300"
                [class.border-s]="!isFirst"
                [class.border-e]="!isLast"
                [attr.colspan]="column.childrenLength ?? 1">
                <div
                  class="th-header flex items-center text-neutral-700 justify-center">
                  <span class="text-sm font-medium text-neutral-900">
                    {{ column.header }}
                  </span>
                </div>
              </th>
            </ng-container>
          }
        </tr>
      }

      <tr class="border-b border-neutral-200">
        @if (selectable()) {
          <th class="px-lg py-sm">
            <label class="checkbox-container flex items-start">
              <input
                type="checkbox"
                class="checkbox"
                [checked]="isAllSelected"
                (change)="toggleAll($event)"
                [attr.name]=""
                [id]="id() + '_master_select'" />
              <span class="checkmark p-sm"></span>
            </label>
          </th>
        }

        @for (column of displayedColumns; track $index) {
          <ng-container>
            <th
              class="px-lg py-md text-sm font-medium border-neutral-300"
              [class.border-s]="column.border?.inlineStart"
              [class.border-e]="column.border?.inlineEnd">
              <div class="th-header flex items-center text-neutral-700">
                <span
                  [class]="column.headerClass ?? ''"
                  class="font-medium text-neutral-900"
                  [class.text-sm]="!parentColumns().length"
                  [class.text-xs]="parentColumns().length"
                  [class.me-x-sm]="column.isRequired"
                  [class.required-header]="column.isRequired">
                  {{ column.header }}
                </span>
              </div>
            </th>
          </ng-container>
        }
      </tr>
    </thead>

    <!-- table body -->
    <tbody>
      @if (dataSource().length && !loading()) {
        @for (row of dataSource(); track $index; let rowIndex = $index) {
          <ng-container
            [ngTemplateOutlet]="tableRow"
            [ngTemplateOutletContext]="{ $implicit: row, i: rowIndex }">
          </ng-container>
        }
      } @else if (!dataSource().length && !loading()) {
        <tr>
          <td [attr.colspan]="columns().length" class="pt-8xl empty-table">
            <div class="flex flex-col justify-center items-center">
              <img
                class=""
                width="64px"
                height="64px"
                [src]="noDataImg()"
                alt="No Data" />
              @if (noDataTitle()) {
                <h6 class="text-xl font-semibold text-neutral-900 mt-lg">
                  {{ noDataTitle() }}
                </h6>
              }

              @if (noDataDescription()) {
                <p class="text-neutral-500 text-sm font-medium mt-sm">
                  {{ noDataDescription() }}
                </p>
              }
            </div>
          </td>
        </tr>
      } @else {
        <tr>
          <td [attr.colspan]="columns().length" class="pt-8xl empty-table">
            <div class="flex flex-col justify-center items-center">
              <lib-loader></lib-loader>
            </div>
          </td>
        </tr>
      }
    </tbody>
  </table>
</div>

<!-- table body in case of there is data -->
<ng-template #tableRow let-row let-rowIndex="i">
  <tr class="border-b border-neutral-200" [class]="rowStyle()">
    @if (selectable()) {
      <td class="px-lg py-sm" [class]="cellStyle()">
        <label class="checkbox-container flex items-start">
          <input
            type="checkbox"
            class="checkbox"
            [checked]="isSelected(row)"
            (change)="toggleElement($event, row)"
            [attr.name]=""
            [id]="rowIndex + '_row_select'" />
          <span class="checkmark p-sm"></span>
        </label>
      </td>
    }

    <!-- dynamic cells -->
    @for (column of displayedColumns; track $index; let isFirst = $first) {
      <td
        class="border-neutral-300"
        [class]="(cellStyle() + ' ' + (column.cellClass ?? '')).trim()"
        [class.border-s]="column.border?.inlineStart"
        [class.border-e]="column.border?.inlineEnd"
        [class.bg-primary-50]="
          row.expand && row.isTopLevel && row.children?.length
        "
        [class.px-lg]="!column.customizeCellStyle"
        [class.py-sm]="!column.customizeCellStyle"
        [class.text-neutral-900]="!column.customizeCellStyle"
        [class.text-sm]="!column.customizeCellStyle"
        [class.font-semibold]="!column.customizeCellStyle">
        <!-- expandable feature -->
        <div class="flex items-center gap-md relative">
          @if (row.children?.length && isFirst) {
            @if (row.expand) {
              @if (row.isTopLevel) {
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  class="inline-block shrink-0 cursor-pointer text-primary-500"
                  (click)="toggleChildren(row)"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 12.5H16"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M9 22.5H15C20 22.5 22 20.5 22 15.5V9.5C22 4.5 20 2.5 15 2.5H9C4 2.5 2 4.5 2 9.5V15.5C2 20.5 4 22.5 9 22.5Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              } @else {
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  class="inline-block shrink-0 cursor-pointer text-primary-500"
                  (click)="toggleChildren(row)"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6 12.5H18"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              }
            } @else {
              @if (row.isTopLevel) {
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  class="inline-block shrink-0 cursor-pointer text-primary-500"
                  fill="none"
                  (click)="toggleChildren(row)"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 12H16"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M12 16V8"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              } @else {
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  class="inline-block shrink-0 cursor-pointer text-primary-500"
                  (click)="toggleChildren(row)"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6 12.5H18"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M12 18.5V6.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              }
            }
          }

          <ng-container
            [ngTemplateOutlet]="bodyTemplateRef()[$index]"
            [ngTemplateOutletContext]="{ $implicit: row, i: rowIndex }">
          </ng-container>
        </div>
      </td>
    }
  </tr>

  <!-- recursive row children part -->
  @if (row.children?.length && row.expand) {
    @for (
      rowChildren of row.children;
      track $index;
      let rowChildrenIndex = $index
    ) {
      <ng-container
        [ngTemplateOutlet]="tableRow"
        [ngTemplateOutletContext]="{
          $implicit: rowChildren,
          i: rowChildrenIndex,
        }" />
    }
  }
</ng-template>
